import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, CheckCircle2, XCircle, ExternalLink, Search, ShieldCheck, AlertTriangle, ScanSearch } from 'lucide-react';

const SEV = {
  high: { cls: 'border-red-300 bg-red-50 text-red-800', icon: XCircle },
  medium: { cls: 'border-amber-300 bg-amber-50 text-amber-800', icon: AlertTriangle },
  low: { cls: 'border-sky-300 bg-sky-50 text-sky-800', icon: AlertTriangle },
};

// Lightweight SEO indexing panel: sitemap count + IndexNow log + full audit.
const IndexingTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/seo/indexing-overview');
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const runAudit = async () => {
    setAuditing(true);
    try {
      const r = await api.get('/admin/seo/audit');
      setAudit(r.data);
    } catch {
      setAudit(null);
    } finally {
      setAuditing(false);
    }
  };

  useEffect(() => { load(); runAudit(); }, []);

  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const sm = data?.sitemap;
  const idx = data?.indexnow;

  return (
    <div className='space-y-5' data-testid='indexing-tab'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-lg font-semibold'>Search &amp; AI Indexing</h2>
          <p className='text-sm text-muted-foreground'>Sitemap coverage and IndexNow instant-submission activity.</p>
        </div>
        <Button variant='outline' size='sm' onClick={load} disabled={loading} data-testid='indexing-refresh'>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {loading && !data ? (
        <div className='py-10 text-center text-muted-foreground'>Loading…</div>
      ) : !data ? (
        <Card className='p-6 text-sm text-muted-foreground'>Could not load indexing overview.</Card>
      ) : (
        <>
          {/* Sitemap */}
          <Card className='p-5' data-testid='indexing-sitemap-card'>
            <div className='flex items-center justify-between mb-3'>
              <div className='font-semibold text-sm'>Sitemap</div>
              <a href={`${backend}${sm?.url}`} target='_blank' rel='noreferrer'
                 className='text-xs inline-flex items-center gap-1 text-primary hover:underline' data-testid='indexing-sitemap-link'>
                View sitemap.xml <ExternalLink className='h-3 w-3' />
              </a>
            </div>
            <div className='text-3xl font-bold' data-testid='indexing-sitemap-total'>{sm?.total ?? 0}</div>
            <div className='text-xs text-muted-foreground mb-3'>total indexable URLs</div>
            <div className='grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs'>
              {Object.entries(sm?.breakdown || {}).map(([k, v]) => (
                <div key={k} className='rounded border bg-muted/40 px-2 py-1.5 flex items-center justify-between'>
                  <span className='capitalize text-muted-foreground'>{k.replace(/_/g, ' ')}</span>
                  <span className='font-semibold'>{v}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* IndexNow */}
          <Card className='p-5' data-testid='indexing-indexnow-card'>
            <div className='flex items-center justify-between mb-3'>
              <div className='font-semibold text-sm'>IndexNow (Bing · Yandex · Naver · Seznam · Brave)</div>
              <a href={`${backend.replace(/\/$/, '')}`} className='hidden' aria-hidden />
            </div>
            <div className='flex flex-wrap gap-2 mb-4'>
              <span className='inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800' data-testid='indexing-ok-count'>
                <CheckCircle2 className='h-3.5 w-3.5' /> {idx?.recent_ok ?? 0} accepted
              </span>
              <span className='inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-red-300 bg-red-50 text-red-800' data-testid='indexing-fail-count'>
                <XCircle className='h-3.5 w-3.5' /> {idx?.recent_failed ?? 0} failed
              </span>
              <span className='inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border bg-muted text-muted-foreground'>
                key file: <code className='font-mono'>{idx?.key_file}</code>
              </span>
            </div>

            <div className='text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2'>Recent submissions</div>
            {(!idx?.log || idx.log.length === 0) ? (
              <div className='text-sm text-muted-foreground flex items-center gap-2'>
                <Search className='h-4 w-4' /> No submissions yet. They appear automatically when a listing, category, or location page is published, updated, or deleted.
              </div>
            ) : (
              <div className='max-h-80 overflow-auto rounded border divide-y'>
                {idx.log.map((row, i) => (
                  <div key={i} className='flex items-center gap-2 px-3 py-2 text-xs' data-testid='indexing-log-row'>
                    {row.ok
                      ? <CheckCircle2 className='h-4 w-4 text-emerald-600 shrink-0' />
                      : <XCircle className='h-4 w-4 text-red-600 shrink-0' />}
                    <span className='font-mono truncate flex-1'>{row.url}</span>
                    <span className='text-muted-foreground shrink-0'>{row.status || '—'}</span>
                    <span className='text-muted-foreground shrink-0'>{row.ts ? new Date(row.ts).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Technical SEO audit */}
          <Card className='p-5' data-testid='indexing-audit-card'>
            <div className='flex items-center justify-between mb-3'>
              <div className='font-semibold text-sm flex items-center gap-2'>
                <ScanSearch className='h-4 w-4 text-primary' /> Technical SEO audit
              </div>
              <Button variant='outline' size='sm' onClick={runAudit} disabled={auditing} data-testid='indexing-run-audit'>
                <RefreshCw className={`h-4 w-4 mr-1.5 ${auditing ? 'animate-spin' : ''}`} /> Run audit
              </Button>
            </div>

            {auditing && !audit ? (
              <div className='py-6 text-center text-muted-foreground text-sm'>Running audit…</div>
            ) : !audit ? (
              <div className='text-sm text-muted-foreground'>Audit unavailable.</div>
            ) : audit.issues.length === 0 ? (
              <div className='flex items-center gap-2 text-sm text-emerald-700' data-testid='audit-clean'>
                <ShieldCheck className='h-5 w-5' /> All checks passed — no SEO issues detected.
              </div>
            ) : (
              <>
                <div className='flex flex-wrap gap-2 mb-3' data-testid='audit-summary'>
                  <span className='inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-red-300 bg-red-50 text-red-800'>
                    <XCircle className='h-3.5 w-3.5' /> {audit.summary.high} high
                  </span>
                  <span className='inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800'>
                    <AlertTriangle className='h-3.5 w-3.5' /> {audit.summary.medium} medium
                  </span>
                  <span className='inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-sky-300 bg-sky-50 text-sky-800'>
                    <AlertTriangle className='h-3.5 w-3.5' /> {audit.summary.low} low
                  </span>
                </div>
                <div className='space-y-2'>
                  {audit.issues.map((it) => {
                    const sev = SEV[it.severity] || SEV.low;
                    const Icon = sev.icon;
                    return (
                      <div key={it.key} className={`rounded border px-3 py-2 ${sev.cls}`} data-testid={`audit-issue-${it.key}`}>
                        <div className='flex items-center gap-2 text-sm font-medium'>
                          <Icon className='h-4 w-4 shrink-0' />
                          <span className='flex-1'>{it.label}</span>
                          <span className='font-bold'>{it.count}</span>
                        </div>
                        <div className='text-xs mt-1 opacity-90'>{it.fix}</div>
                        {it.samples?.length > 0 && (
                          <div className='text-[11px] mt-1 font-mono opacity-70 truncate'>
                            e.g. {it.samples.map(s => s.slug || s.name || s.title || s.id).slice(0, 4).join(', ')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {audit.note && <div className='text-[11px] text-muted-foreground mt-3 leading-relaxed'>{audit.note}</div>}
              </>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default IndexingTab;
